import { createFileRoute, Outlet, useLocation, useParams } from '@tanstack/react-router';

import { RequireAuth } from '@/components/layout/RequireAuth';
import { EngagementShell } from '@/components/layout/engagement-shell';
import { EngagementActivityStrip } from '@/components/layout/engagement-activity-strip';
import { useEngagementLiveRefresh } from '@/lib/api/engagement-activity';
import { useEngagementQuery } from '@/lib/api/engagements';
import { reviewRedirectTarget } from '@/lib/engagement-modules';

/**
 * Engagement detail layout (T-0096 / E10).
 *
 * Wraps every `/engagements/:id/...` route in the `<EngagementShell>`
 * which owns the per-engagement sub-nav + action bar (capture snapshot /
 * generate PDF / posture chip) per the prototype IA in README §4.
 *
 * The previous flat `Tabs` strip from the scaffold has been replaced by
 * the prototype's left-rail sub-nav with collapsible Contract / Health /
 * Go-Live groups.
 */
export const Route = createFileRoute('/engagements/$engagementId')({
  component: EngagementLayout,
});

function EngagementLayout() {
  return (
    <RequireAuth>
      <EngagementShell>
        <EngagementLive />
        <Outlet />
      </EngagementShell>
    </RequireAuth>
  );
}

/**
 * Live refresh for every screen under this engagement: polls the activity
 * pulse, refetches the engagement's cached queries when it moves, and
 * shows the status strip while AI work is in flight or proposals wait.
 * Lives inside RequireAuth so it never polls for a signed-out user.
 */
function EngagementLive() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId' });
  const { pathname } = useLocation();
  const activity = useEngagementLiveRefresh(engagementId);
  // The review queue itself (and the documents/review alias that opens it)
  // never shows "open review": that would point at the page already open.
  const onReviewQueue = /\/(proposals|documents\/review)\/?$/.test(pathname);
  // An engagement with no IV&V module has no review queue to point at (its
  // route redirects to Discovery or Selection). The engagement read is the
  // same cached one every screen under this engagement uses.
  const engagement = useEngagementQuery(engagementId);
  const reviewQueueAvailable = engagement.data
    ? reviewRedirectTarget(engagement.data.modules_enabled) === null
    : true;
  return (
    <EngagementActivityStrip
      engagementId={engagementId}
      activity={activity}
      onReviewQueue={onReviewQueue}
      reviewQueueAvailable={reviewQueueAvailable}
    />
  );
}
